import React, { useState, useEffect, useCallback } from 'react';
import LandingPage from './components/LandingPage';
import DemoScopePage from './features/scope_module/DemoScopePage';
import './TouchBase.css';
import './App.css';

const IDLE_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds

function App() {
  // Check if coming from main site - skip landing page
  const fromMain = new URLSearchParams(window.location.search).get('from') === 'main';

  const [currentPage, setCurrentPage] = useState(fromMain ? 'loading' : 'landing');
  const [lastActivity, setLastActivity] = useState(Date.now());

  // Legacy support
  const showDemo = currentPage === 'demo';

  // Handle transition from main site
  useEffect(() => {
    if (fromMain && currentPage === 'loading') {
      // Clean up the URL
      window.history.replaceState({}, '', window.location.pathname);
      // Show the completed fingerprint briefly, then transition to demo
      const timer = setTimeout(() => {
        setCurrentPage('demo');
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [fromMain, currentPage]);

  // Reset activity timer on user interaction
  const resetActivityTimer = useCallback(() => {
    setLastActivity(Date.now());
  }, []);

  // Check for idle timeout
  useEffect(() => {
    if (!showDemo) return;

    const checkIdle = setInterval(() => {
      const idleTime = Date.now() - lastActivity;
      if (idleTime >= IDLE_TIMEOUT) {
        setCurrentPage('landing');
      }
    }, 10000); // Check every 10 seconds

    return () => clearInterval(checkIdle);
  }, [showDemo, lastActivity]);

  // Add event listeners for activity tracking when in demo mode
  useEffect(() => {
    if (!showDemo) return;

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'wheel'];
    events.forEach(event => {
      window.addEventListener(event, resetActivityTimer, { passive: true });
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, resetActivityTimer);
      });
    };
  }, [showDemo, resetActivityTimer]);

  const handleStartDemo = () => {
    setLastActivity(Date.now());
    setCurrentPage('demo');
  };

  const handleExitDemo = () => {
    // If user came from main site, redirect back there
    if (fromMain) {
      window.location.href = 'https://touchbaseforensics.com';
    } else {
      setCurrentPage('landing');
    }
  };

  const renderCurrentPage = () => {
    switch (currentPage) {
      case 'loading':
        return (
          <div className="app-transition">
            <div className="fp-image-container">
              <img
                src="/fingerprint-transition.png"
                alt=""
                className="fp-transition-image"
              />
              <div className="fp-scan-line"></div>
            </div>

            <div className="app-transition-content">
              <span className="app-transition-text">Initializing SCOPE</span>
              <div className="app-transition-dots">
                <span></span><span></span><span></span>
              </div>
            </div>
          </div>
        );
      case 'demo':
        return <DemoScopePage onExit={handleExitDemo} />;
      default:
        return <LandingPage onStartDemo={handleStartDemo} />;
    }
  };

  return (
    <div className="app">
      {renderCurrentPage()}
    </div>
  );
}

export default App;
